import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageContainer, PageHeader, StatCard, EmptyState } from "@/components/Page";
import { StatusBadge, CategoryBadge } from "@/components/Badges";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Plus } from "lucide-react";

export default function ResidentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [estate, setEstate] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [t, e] = await Promise.all([
          api.get("/tickets"),
          api.get("/estates"),
        ]);
        setTickets(t.data);
        setEstate(e.data[0] || null);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);

  const open = tickets.filter((t) => ["pending", "ongoing", "on_review"].includes(t.status)).length;
  const done = tickets.filter((t) => t.status === "complete").length;

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          eyebrow={estate?.name || "Resident"}
          title={`Hi, ${user?.name?.split(" ")[0] || "there"}.`}
          description="Raise and track issues about your unit or the estate."
          actions={
            <Button
              onClick={() => navigate("/r/tickets/new")}
              data-testid="raise-ticket-cta"
              className="rounded-none bg-black hover:bg-zinc-800 h-10 px-4 uppercase tracking-wider text-xs font-semibold"
            >
              <Plus className="h-4 w-4" /> Raise a Ticket
            </Button>
          }
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard label="Total Tickets" value={tickets.length} testid="stat-tickets" />
          <StatCard label="Open" value={open} testid="stat-open" />
          <StatCard label="Resolved" value={done} testid="stat-complete" />
        </div>

        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
            <div className="label-eyebrow">Your Recent Tickets</div>
            <Link to="/r/tickets" className="text-xs font-semibold uppercase tracking-wider underline underline-offset-4" data-testid="view-all-tickets-link">View all →</Link>
          </div>
          {tickets.length === 0 ? (
            <EmptyState
              title="No tickets yet"
              description="Have an issue with your unit? Raise a ticket and the estate manager will follow up."
              action={<Button onClick={() => navigate("/r/tickets/new")} data-testid="empty-raise-ticket" className="rounded-none bg-black hover:bg-zinc-800 uppercase tracking-wider text-xs font-semibold"><Plus className="h-4 w-4" /> Raise a Ticket</Button>}
            />
          ) : (
            <table className="w-full text-sm min-w-[640px]">
              <thead className="border-b border-zinc-200">
                <tr className="text-left">
                  <th className="p-4 label-eyebrow font-semibold">Title</th>
                  <th className="p-4 label-eyebrow font-semibold">Category</th>
                  <th className="p-4 label-eyebrow font-semibold">Status</th>
                  <th className="p-4 label-eyebrow font-semibold">Created</th>
                </tr>
              </thead>
              <tbody>
                {tickets.slice(0, 6).map((t) => (
                  <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`ticket-row-${t.id}`}>
                    <td className="p-4"><Link to={`/tickets/${t.id}`} className="font-semibold hover:underline underline-offset-4">{t.title}</Link></td>
                    <td className="p-4"><CategoryBadge category={t.category} /></td>
                    <td className="p-4"><StatusBadge status={t.status} /></td>
                    <td className="p-4 text-zinc-500 text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </PageContainer>
    </AppShell>
  );
}
